import type { DepartureDto } from "@tripcart/shared";

/** The open/closed and "guaranteed to run" rules live in `@tripcart/shared`
 *  (`departure-scarcity.ts`), shared with the Departure Dates block. */
export { isDepartureOpen, isGuaranteedToRun } from "@tripcart/shared";

/**
 * What one departure's availability actually is.
 *
 * i18n-free structured data, like `describeUnit` / `stepBlockedBy` in
 * `_capacity.ts` — the caller owns the wording, this owns the decision. That
 * split is what lets this be unit-tested without a translator.
 *
 * THREE states here, and the missing fourth is the interesting part.
 *
 *  - `sold-out` — genuinely nothing left, or the operator closed the date.
 *  - `uncapped` — the operator set no capacity limit. There is no number to
 *                 show and no scarcity to imply; `spotsLeft` is `null`, never
 *                 a large sentinel.
 *  - `seats`    — a real, bounded count, stated as a plain fact.
 *
 * WHERE `unknown` WENT. Invariant 5 in this directory's CLAUDE.md names four
 * honest states, the fourth being "no response yet, or the request failed".
 * This module carried it as a variant for a while — with a `StatusPill` arm, an
 * `isBookable` arm and two call-site guards — and NONE of them could ever run:
 * the invariant's `unknown` is keyed on `hasLiveData`, which lives on
 * `PackageDto` (the package-card surface), and `DepartureDto` has no such
 * field. `resolveDepartureStatus` returns one of the three above, always.
 *
 * The state is real on this surface; it just is not per-ROW. It is the whole
 * SECTION: `departures === null` means the window has not answered, and
 * `DeparturesSection` returns "Checking which dates are still open…" before a
 * single row renders. By the time this function is called, live data exists.
 *
 * That is why the variant is gone rather than kept "just in case". Its only
 * test built the literal by hand, so it proved the enum branch and not the
 * invariant — the exact shape the root CLAUDE.md warns about, where a spec
 * that verifies one instance of an invariant reads as if it verifies the
 * invariant. If `DepartureDto` ever gains a liveness field, add the state back
 * WITH a path that can produce it.
 *
 * THREE STATES, NOT FIVE — and again that is about this TYPE, not about what
 * the page shows. The scarcity bands ("Filling up fast" ≤5, "Almost sold out"
 * ≤2) are
 * real and they DO render; they are a presentation choice made from
 * `spotsLeft` in `status-pill.tsx`, from the same two constants the package
 * card reads (`_packages/_availability-pill.ts`). Keeping them out of the type
 * is what lets this stay i18n-free and unit-testable without a translator.
 *
 * This docstring argued the opposite until 2026-09-15 — that there must be no
 * bands anywhere, quoting a line in `_package-card.tsx` that no longer
 * described that file. The premise was that a threshold is a guess made on the
 * operator's behalf. It is not: the style guide specifies both cut-offs and
 * both strings, so stating them reports the guide. What survives from that
 * reasoning is the narrower rule — we invent no band the guide does not
 * define, which is why anything above five is a plain count.
 *
 * The thing both files were right to fear is real and is now prevented by
 * construction rather than by agreement: the list and the package card sit on
 * one screen, so the same number must not wear two levels of urgency. They
 * cannot, because neither owns the thresholds.
 */
export type DepartureStatus =
  | { kind: "sold-out"; reason?: string }
  | { kind: "uncapped" }
  | { kind: "seats"; spotsLeft: number };

export function resolveDepartureStatus(d: DepartureDto): DepartureStatus {
  if (d.isClosed) {
    // `closeReason` is operator-authored free text. Passed through for the
    // caller to show; absent is normal and must not become "Closed by
    // operator", which asserts a reason we were not given.
    return d.closeReason
      ? { kind: "sold-out", reason: d.closeReason }
      : { kind: "sold-out" };
  }
  // `null` is UNCAPPED, not unknown and not zero. Checked before the numeric
  // comparisons below, because `null <= 5` is true in JavaScript and would
  // label every unlimited departure "filling up fast".
  if (d.spotsLeft === null) return { kind: "uncapped" };
  if (d.spotsLeft === 0) return { kind: "sold-out" };
  return { kind: "seats", spotsLeft: d.spotsLeft };
}

/** Whether a status permits booking.
 *
 *  Sold out is the only refusal, because it is the only one of the three that
 *  can reach here. The "never offer a seat we cannot honour" half of invariant
 *  5 is enforced one level up, where `departures === null` stops every row
 *  rendering at all. */
export function isBookable(status: DepartureStatus): boolean {
  return status.kind === "uncapped" || status.kind === "seats";
}
